export const NUMBER_IN_SLICE = /[+\-−]?\d(?:[\d,\u00a0\u202f]*\d)?(?:\.\d+)?/;

export type TScaleLadder = { words: string[]; base: number };
const ATTACHED: TScaleLadder[] = [
  { words: ["K", "M", "B", "T"], base: 1000 },
  { words: ["k", "m", "bn", "tn"], base: 1000 },
  { words: ["k", "mn", "bn", "tn"], base: 1000 }
];
const SPACED_ABBREVIATIONS: TScaleLadder = { words: ["k", "mn", "bn", "tn"], base: 1000 };
const SPACED: TScaleLadder[] = [
  SPACED_ABBREVIATIONS,
  { words: ["thousand", "million", "billion", "trillion"], base: 1000 }
];
const CJK: TScaleLadder = { words: ["万", "億", "兆"], base: 10000 };

export const SCALE_LADDERS: TScaleLadder[] = [...ATTACHED, ...SPACED, CJK];

export type TScale = { ladder: TScaleLadder; step: number; word: string; gap: string };

export const scaleSize = (ladder: TScaleLadder, step: number): number => ladder.base ** (step + 1);

export const CURRENCY_CODES = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CNY",
  "RMB",
  "CHF",
  "CAD",
  "AUD",
  "NZD",
  "HKD",
  "SGD",
  "INR",
  "KRW",
  "SEK",
  "NOK",
  "DKK",
  "BRL",
  "MXN",
  "ZAR",
  "AED",
  "SAR"
];

const UNIT_CURRENCY = new RegExp(`^(?:(?:${CURRENCY_CODES.join("|")})(?![A-Za-z])|[A-Z]{1,3}\\$|[$€£¥])`);
const UNIT_SCALE = /^(?:[A-Za-z]+|[万億兆])/;

const scaleWordOf = (word: string, afterCurrency: boolean): { ladder: TScaleLadder; step: number } | null => {
  if (word === "m" && !afterCurrency) return null;
  for (const ladder of [...ATTACHED, ...SPACED, CJK]) {
    const step = ladder.words.indexOf(word);
    if (step >= 0) return { ladder, step };
  }
  return null;
};

export const readUnit = (
  unit: string | null | undefined
): { currency?: string; scale?: { ladder: TScaleLadder; step: number; word: string }; suffix: string } => {
  const text = (unit ?? "").trim();
  const currency = UNIT_CURRENCY.exec(text)?.[0];
  const afterCurrency = currency ? text.slice(currency.length).replace(/^[ \u00a0]+/, "") : text;
  const word = UNIT_SCALE.exec(afterCurrency)?.[0];
  const scale = word ? scaleWordOf(word, !!currency) : null;
  const suffix = scale && word ? afterCurrency.slice(word.length) : currency ? text.slice(currency.length) : text;
  return { ...(currency ? { currency } : {}), ...(scale && word ? { scale: { ...scale, word } } : {}), suffix };
};

const escapeRe = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const unitSuffix = (rest: string, unit: string | null | undefined): string => {
  const words = readUnit(unit)
    .suffix.trim()
    .split(/[ \u00a0]+/)
    .filter(Boolean);
  if (words.length === 0 || words.join("") === "%") return "";
  return new RegExp(`^[ \u00a0]?${words.map(escapeRe).join("[ \u00a0]")}(?![\\w])`).exec(rest)?.[0] ?? "";
};

export const scaleAfter = (suffix: string, unit?: string | null): TScale | null => {
  if (unit && unitSuffix(suffix, unit)) return null;
  const cjk = /^[万億兆]/.exec(suffix);
  if (cjk) return { ladder: CJK, step: CJK.words.indexOf(cjk[0]), word: cjk[0], gap: "" };
  const match = /^([ \u00a0]?)([A-Za-z]+)(?![\p{L}\p{N}])/u.exec(suffix);
  if (!match) return null;
  const [, gap, word] = match;
  for (const ladder of gap ? SPACED : ATTACHED) {
    const step = ladder.words.indexOf(word);
    if (step < 0 || (gap && ladder === SPACED_ABBREVIATIONS && step === 0)) continue;
    return { ladder, step, word, gap };
  }
  return null;
};

export const scaleOfUnit = (unit: string | null | undefined): number => {
  const { scale } = readUnit(unit);
  return scale ? scaleSize(scale.ladder, scale.step) : 1;
};

export const magnitudeOf = (number: string): number =>
  Number(number.replace(/^[+\-−]/, "").replace(/[,\u00a0\u202f]/g, ""));

export type TWrittenFigure = {
  number: number;
  places: number;
  negative: boolean;
  scale: number;
  scaled: boolean;
};

const SIGN_BEFORE = new RegExp(`[-−]\\s*(?:[A-Z]{1,3}\\$|[$€£¥]|(?:${CURRENCY_CODES.join("|")})[ \\u00a0])?$`);

export const readWritten = (written: string, unit?: string | null): TWrittenFigure | null => {
  const trimmed = written.trim();
  const accounting = /^\((.*)\)$/.exec(trimmed);
  const inner = accounting ? accounting[1] : trimmed;
  const match = NUMBER_IN_SLICE.exec(inner);
  if (!match) return null;
  const digits = match[0];
  const scale = scaleAfter(inner.slice(match.index + digits.length), unit);
  const number = magnitudeOf(digits);
  if (!Number.isFinite(number)) return null;
  return {
    number,
    places: (digits.split(".")[1] ?? "").length,
    negative: !!accounting || /^[-−]/.test(digits) || SIGN_BEFORE.test(inner.slice(0, match.index)),
    scale: scale ? scaleSize(scale.ladder, scale.step) : 1,
    scaled: !!scale
  };
};

export const readsUnscaled = (written: number, value: number, unitScale: number): boolean => {
  if (unitScale === 1 || written === 0 || value === 0) return false;
  const near = Math.abs(Math.log(Math.abs(written) / Math.abs(value)));
  const whole = Math.abs(Math.log(Math.abs(written) / (Math.abs(value) * unitScale)));
  return whole < near;
};

const PERCENT = "percent";
const MONEY = "money";
const OTHER = "other";
const ALL_CODES = [...CURRENCY_CODES, "THB"];
const CURRENCY_SYMBOL = /[$£€¥฿]/;
const POINTS_WORD = /\b(?:percentage|percent|pct)\s+(?:points?|pts?)\b|\b(?:bps|basis\s+points?|pp)\b/i;
const PERCENT_WORD = /\b(?:percent|per\s?cent|pct)\b/i;
const CURRENCY_WORD = new RegExp(`\\b(?:dollars?|euros?|pounds?|sterling|yen|baht|${ALL_CODES.join("|")})\\b`, "i");
const POINTS_UNIT = /^(?:%|percentage|percent|pct)[\s_-]+(?:points?|pts?)(?![A-Za-z])/i;
const PERCENT_UNIT = /^(?:%|pct|percentage|percent)(?![A-Za-z])/i;
const CURRENCY_UNIT = new RegExp(`(?:\\b(?:${ALL_CODES.join("|")})\\b|US\\$|[$£€¥฿])`, "i");
const FRACTION_UNIT = /^(?:fraction|ratio|share)\b/i;

const unitClass = (unit: string): string => {
  const trimmed = unit.trim();
  if (POINTS_UNIT.test(trimmed)) return OTHER;
  if (PERCENT_UNIT.test(trimmed)) return PERCENT;
  return CURRENCY_UNIT.test(trimmed) ? MONEY : OTHER;
};

const writtenClass = (written: string): string => {
  if (POINTS_WORD.test(written)) return OTHER;
  if (written.includes("%") || PERCENT_WORD.test(written)) return PERCENT;
  return CURRENCY_WORD.test(written) || CURRENCY_SYMBOL.test(written) ? MONEY : OTHER;
};

const CODE_WORD = new RegExp(`(?<![A-Za-z])(${ALL_CODES.join("|")})(?![A-Za-z])`);
const CURRENCY_SIGNS: [string, string][] = [
  ["US$", "USD"],
  ["HK$", "HKD"],
  ["NZ$", "NZD"],
  ["A$", "AUD"],
  ["C$", "CAD"],
  ["S$", "SGD"],
  ["$", "USD"],
  ["€", "EUR"],
  ["£", "GBP"],
  ["¥", "JPY"],
  ["฿", "THB"]
];
const CURRENCY_NAMES: [RegExp, string][] = [
  [/\bdollars?\b/i, "USD"],
  [/\beuros?\b/i, "EUR"],
  [/\b(?:pounds?|sterling)\b/i, "GBP"],
  [/\byen\b/i, "JPY"],
  [/\bbaht\b/i, "THB"]
];

export const currencyOf = (text: string): string | undefined => {
  const code = CODE_WORD.exec(text)?.[1];
  if (code) return code;
  for (const [sign, named] of CURRENCY_SIGNS) if (text.includes(sign)) return named;
  return CURRENCY_NAMES.find(([word]) => word.test(text))?.[1];
};

const unread = (written: string, unit?: string | null): string => {
  const match = NUMBER_IN_SLICE.exec(written);
  if (!match) return written;
  const end = match.index + match[0].length;
  const scale = scaleAfter(written.slice(end), unit);
  return written.slice(0, match.index) + written.slice(end + (scale ? scale.gap.length + scale.word.length : 0));
};

const statesAKind = (written: string, unit?: string | null): boolean =>
  CURRENCY_SYMBOL.test(written) || written.includes("%") || /[A-Za-z]/.test(unread(written, unit));

const sameKind = (written: string, unit: string | null): boolean => {
  if (!statesAKind(written, unit)) return true;
  const want = unitClass(unit ?? "");
  const got = writtenClass(written);
  if (want === MONEY && got === MONEY) {
    const wanted = currencyOf(unit ?? "");
    const named = currencyOf(written);
    if (wanted && named && wanted !== named) return false;
  }
  return want === got || (isFractionUnit(unit) && got === PERCENT);
};

export const isFractionUnit = (unit: string | null | undefined): boolean => FRACTION_UNIT.test((unit ?? "").trim());

export const writesWholeAmount = (
  written: string,
  number: number,
  value: number | undefined,
  unitScale: number
): boolean =>
  unitScale !== 1 &&
  (writtenClass(written) === MONEY || (value !== undefined && readsUnscaled(number, value, unitScale)));

export const roundHalfUp = (value: number, places: number): number => {
  const factor = 10 ** places;
  const scaled = Math.abs(value) * factor;
  const rounded = Math.floor(scaled + 0.5 + 1e-9 * Math.max(1, scaled)) / factor;
  return value < 0 ? -rounded : rounded;
};

export const writtenReadsFigure = (
  written: string,
  value: number,
  unit?: string | null,
  _dp?: number | null
): boolean => {
  if (!Number.isFinite(value)) return false;
  const read = readWritten(written, unit);
  if (!read) return false;
  if (unit !== undefined && !sameKind(written, unit)) return false;
  const unitScale = unit === undefined ? read.scale : scaleOfUnit(unit);
  const writtenScale =
    read.scaled || unit === undefined
      ? read.scale
      : writesWholeAmount(written, read.number, value, unitScale)
      ? 1
      : unitScale;
  const fraction = isFractionUnit(unit) && written.includes("%") ? 100 : 1;
  const said = (read.negative ? -1 : 1) * read.number;
  const ledger = (value * unitScale * fraction) / writtenScale;
  return Math.abs(roundHalfUp(ledger, read.places) - said) <= 1e-9 * Math.max(1, Math.abs(said));
};

const LETTER = "\\p{L}\\p{Nl}\\p{No}";
const CURRENCY = `(?:[A-Z]{1,3}\\$|[$€£¥]|(?:${CURRENCY_CODES.join("|")})[ \\u00a0])`;
const SCALE = `(?:(?:K|M|B|T|k|m|mn|bn|tn)(?![${LETTER}])|[ \\u00a0](?:mn|bn|tn|thousand|million|billion|trillion)(?![${LETTER}])|[万億兆])`;
const SUFFIX_CHAR = `(?:[${LETTER}%‰°×²³€£¥/'.\\- \\u00a0]|(?<=[${LETTER}])\\d)`;
const SUFFIX_END = `(?:[${LETTER}%‰°×²³€£¥]|(?<=[${LETTER}])\\d)`;
const FIGURE_SPELLING = new RegExp(
  `^[+\\-−]?${CURRENCY}?[+\\-−]?\\d(?:[\\d,\\u00a0\\u202f]*\\d)?(?:\\.\\d+)?${SCALE}?(?:[ \\u00a0]?${SUFFIX_END}(?:${SUFFIX_CHAR}*${SUFFIX_END})?)?$`,
  "u"
);

export const isFigureSpelling = (slice: string): boolean => {
  const accounting = /^\((.*)\)$/.exec(slice);
  return FIGURE_SPELLING.test(accounting ? accounting[1] : slice);
};
